import React, { useContext, useState, useCallback, useMemo } from 'react'

import { FormHandles } from '@unform/core'

import BadgeIsValid from '~/components/BadgeIsValid'
import { FeedbackContext } from '~/components/feedback'
import Panel from '~/components/Panel'
import { FormGroup, Label, Input, InputMask, Select } from '~/components/unform'
import { useAppInfo } from '~/hooks/useAppInfo'
import i from '~/i18n'
import { getInfoAddressByPostalCode } from '~/services/location-service'

import { getPostalCodeConfig } from './helpers'
import RegionField from './RegionField'

type AddressFieldsProps = {
  formRef: React.RefObject<FormHandles> | null | undefined
  title?: string
  descriptionPanel?: string
  hideComplement?: boolean
  noPaddingBody?: boolean
  allRequired?: boolean
  isCollapsible?: boolean
  showBadgeValidate?: boolean
  badgeIsValid?: boolean
  autocompleteByPostalCode?: boolean
  country?: string
}

type HandleBlurCepParams = {
  value: string
  clearError: () => void
  setIsFilled: (value: boolean) => void
}

/**
 * V2 - Versão atualizada do AddressFieldset
 */

const AddressFieldset = ({
  formRef,
  title,
  descriptionPanel = '',
  hideComplement = false,
  noPaddingBody = false,
  allRequired = false,
  isCollapsible = true,
  showBadgeValidate = false,
  badgeIsValid = false,
  autocompleteByPostalCode = false,
  country: countryProp
}: AddressFieldsProps) => {
  const [loadingCep, setLoadingCep] = useState(false)

  const { toast } = useContext(FeedbackContext)
  const { country: countryFromAppInfo } = useAppInfo()

  // Usa o country da prop, se fornecido, senão usa do app info, senão usa 'BR' como padrão
  const country = countryProp || countryFromAppInfo || 'BR'

  const postalCodeConfig = useMemo(
    () => getPostalCodeConfig(country),
    [country]
  )

  const handleBlurCep = useCallback(
    async ({ value, clearError, setIsFilled }: HandleBlurCepParams) => {
      const normalizedValue = postalCodeConfig.normalize(value)

      setLoadingCep(true)

      if (!postalCodeConfig.validate(value)) {
        const postalCodeLabel = i.t(
          'c:components.AddressFieldset.labels.postalCode'
        )

        const toastMessage = i.t(
          'c:components.AddressFieldset.messages.postalCodeInvalidLength',
          {
            label: postalCodeLabel,
            length: String(postalCodeConfig.length)
          }
        )

        toast.warning(toastMessage)

        setLoadingCep(false)

        return
      }

      try {
        const response = await getInfoAddressByPostalCode(normalizedValue)

        const {
          address_postal_code,
          address_neighborhood,
          address_street,
          address_city,
          address_state,
          address_country,
          latitude,
          longitude
        } = response.data

        formRef.current.setData({
          postcode: address_postal_code,
          neighborhood: address_neighborhood,
          street: address_street,
          city: address_city,
          region: address_state,
          country: address_country || 'Brasil',
          latitude,
          longitude
        })

        formRef.current.setErrors({
          postcode: '',
          neighborhood: '',
          street: '',
          city: '',
          region: '',
          country: '',
          number: ''
        })

        setIsFilled(true)
        clearError()
      } catch (err) {
        const postalCodeLabel = i.t(
          'c:components.AddressFieldset.labels.postalCode'
        )

        const toastMessage = i.t(
          'c:components.AddressFieldset.messages.postalCodeNotFound',
          {
            label: postalCodeLabel
          }
        )

        toast.critical(toastMessage)
      } finally {
        setLoadingCep(false)
      }
    },
    [formRef, toast, postalCodeConfig]
  )

  const panelTitle = title ?? i.t('c:components.AddressFieldset.title')

  const postalCodeLabel: string = i.t(
    'c:components.AddressFieldset.labels.postalCode'
  )

  const postalCodePlaceholder = i.t(
    'c:components.AddressFieldset.placeholders.postalCode',
    {
      label: postalCodeLabel.toLowerCase(),
      example: postalCodeConfig.placeholder
    }
  )

  return (
    <fieldset>
      <Panel
        title={panelTitle}
        className="form-grid"
        noPaddingBody={noPaddingBody}
        description={descriptionPanel}
        isCollapsible={isCollapsible}
        iconLeft={
          showBadgeValidate ? <BadgeIsValid isValid={badgeIsValid} /> : null
        }
      >
        {/* Postal Code */}
        <FormGroup>
          <Label isRequired={allRequired} htmlFor="postcode">
            {postalCodeLabel}
          </Label>
          <InputMask
            name="postcode"
            loading={loadingCep}
            format={postalCodeConfig.format}
            placeholder={postalCodePlaceholder}
            required={allRequired}
            onBlurCustom={autocompleteByPostalCode ? handleBlurCep : undefined}
          />
        </FormGroup>
        {/* Street */}
        <FormGroup className="span2 span-start">
          <Label isRequired={allRequired} htmlFor="street">
            {i.t('c:components.AddressFieldset.labels.street')}
          </Label>
          <Input
            name="street"
            placeholder={i.t(
              'c:components.AddressFieldset.placeholders.street'
            )}
            loading={loadingCep}
            disabled={loadingCep}
            required={allRequired}
          />
        </FormGroup>
        {/* Number */}
        <FormGroup>
          <Label isRequired={allRequired} htmlFor="number">
            {i.t('c:components.AddressFieldset.labels.number')}
          </Label>
          <Input
            name="number"
            placeholder={i.t(
              'c:components.AddressFieldset.placeholders.number'
            )}
            required={allRequired}
          />
        </FormGroup>
        {/* Complement */}
        {!hideComplement && (
          <FormGroup className="span2">
            <Label htmlFor="complement">
              {i.t('c:components.AddressFieldset.labels.complement')}
            </Label>
            <Input
              name="complement"
              placeholder={i.t(
                'c:components.AddressFieldset.placeholders.complement'
              )}
            />
          </FormGroup>
        )}
        {/* Neighborhood */}
        <FormGroup className="span2 span-start">
          <Label htmlFor="neighborhood">
            {i.t('c:components.AddressFieldset.labels.neighborhood')}
          </Label>
          <Input
            name="neighborhood"
            placeholder={i.t(
              'c:components.AddressFieldset.placeholders.neighborhood'
            )}
            loading={loadingCep}
            disabled={loadingCep}
          />
        </FormGroup>
        {/* City */}
        <FormGroup className="span2">
          <Label isRequired={allRequired} htmlFor="city">
            {i.t('c:components.AddressFieldset.labels.city')}
          </Label>
          <Input
            name="city"
            placeholder={i.t('c:components.AddressFieldset.placeholders.city')}
            loading={loadingCep}
            disabled={loadingCep}
            required={allRequired}
          />
        </FormGroup>

        {/* Region */}
        <RegionField
          isRequired={allRequired}
          loading={loadingCep}
          country={country}
          name="region"
          maxLength={2}
        />
        {/* Country */}
        <FormGroup className="span2">
          <Label isRequired={allRequired} htmlFor="country">
            {i.t('c:components.AddressFieldset.labels.country')}
          </Label>
          <Select
            disabled
            defaultValue={country}
            name="country"
            placeholder={i.t(
              'c:components.AddressFieldset.placeholders.country'
            )}
            options={[
              {
                id: country,
                value: country,
                label: i.t(`c:countries.${country}`, { defaultValue: country })
              }
            ]}
          />
        </FormGroup>
      </Panel>
    </fieldset>
  )
}

export default AddressFieldset
